import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";

// Sprint 8.B — Quick-Mode-Banner: 1 Headline + 1 Frage + 3 Buttons.
// Auto-Progress nach 5s zu Insight. „Ja" scrollt zur Focus-Tile oder navigiert zu /dashboard/anliegen.

const AUTO_DISMISS_MS = 5000;

export default function QuickModeBanner({ mode, onSwitchToInsight }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (mode !== "quick") return undefined;
    let cancelled = false;
    api
      .get("/cockpit/quick-insight")
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Auto-Dismiss nach 5s wenn kein Klick
  useEffect(() => {
    if (mode !== "quick" || !data) return undefined;
    timerRef.current = setTimeout(() => {
      onSwitchToInsight?.();
    }, AUTO_DISMISS_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [mode, data, onSwitchToInsight]);

  if (mode !== "quick" || !data || dismissed) return null;

  const stopTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const handleYes = () => {
    stopTimer();
    if (data.yes_action === "new_bruecke") {
      navigate(data.yes_target || "/dashboard/anliegen");
      return;
    }
    onSwitchToInsight?.();
    setTimeout(() => {
      if (data.yes_target) {
        const el = document.querySelector(`[data-testid="${data.yes_target}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 120);
  };

  const handleLater = () => {
    stopTimer();
    setDismissed(true);
    onSwitchToInsight?.();
  };

  const handleMore = () => {
    stopTimer();
    onSwitchToInsight?.();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.4 }}
        data-testid="quick-mode-banner"
        className="mb-6 rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 to-[#4563BB] text-white px-5 sm:px-6 py-5 sm:py-6 shadow-md relative overflow-hidden"
      >
        <Sparkles
          className="absolute -right-4 -top-4 w-20 h-20 opacity-10"
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={handleLater}
          aria-label="Quick-Mode schließen"
          data-testid="quick-mode-close"
          className="absolute top-3 right-3 text-white/60 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>

        <p
          data-testid="quick-mode-headline"
          className="text-base sm:text-lg font-extrabold tracking-tight leading-snug pr-6"
        >
          {data.headline}
        </p>
        <p
          data-testid="quick-mode-question"
          className="mt-1 text-sm text-white/90"
        >
          {data.question}
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleYes}
            data-testid="quick-mode-yes"
            className="rounded-lg bg-white text-[#4563BB] hover:bg-slate-100 font-bold px-4 py-2 text-sm"
          >
            Ja
          </button>
          <button
            type="button"
            onClick={handleLater}
            data-testid="quick-mode-later"
            className="rounded-lg border border-white/40 hover:bg-white/10 text-white font-semibold px-4 py-2 text-sm"
          >
            Später
          </button>
          <button
            type="button"
            onClick={handleMore}
            data-testid="quick-mode-more"
            className="rounded-lg text-white/80 hover:text-white font-semibold px-3 py-2 text-sm underline-offset-2 hover:underline"
          >
            Mehr im Mitgliederbereich
          </button>
        </div>

        {/* dezente Progress-Linie für 5s Auto-Dismiss */}
        <motion.div
          initial={{ width: "100%" }}
          animate={{ width: "0%" }}
          transition={{ duration: AUTO_DISMISS_MS / 1000, ease: "linear" }}
          data-testid="quick-mode-progress"
          className="absolute bottom-0 left-0 h-0.5 bg-white/40"
        />
      </motion.div>
    </AnimatePresence>
  );
}
